import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EventIntakeFill from '../components/EventIntakeFill';
import { useAuth } from '../context/AuthContext';
import { fetchEvent, fetchIntakeForm, type EventRow, type IntakeField, type IntakeForm } from '../api/events';
import '../components/EventIntakeFormTab.css';
import './Page.css';

const EventIntakeFullScreen: React.FC = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const idNum = eventId ? parseInt(eventId, 10) : NaN;

  const [event, setEvent] = useState<EventRow | null>(null);
  const [form, setForm] = useState<IntakeForm | null>(null);
  const [fields, setFields] = useState<IntakeField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const back = useCallback(() => {
    navigate(`/events/${idNum}?tab=${encodeURIComponent('Intake Form')}`);
  }, [navigate, idNum]);

  const load = useCallback(async () => {
    if (!user?.id || !Number.isInteger(idNum) || idNum < 1) return;
    setLoading(true);
    setError('');
    try {
      const [ev, { form: f, fields: flds }] = await Promise.all([
        fetchEvent(idNum, user.id),
        fetchIntakeForm(idNum, user.id),
      ]);
      setEvent(ev);
      setForm(f);
      setFields(flds);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load intake form.');
    } finally {
      setLoading(false);
    }
  }, [user?.id, idNum]);

  useEffect(() => {
    void load();
  }, [load]);

  const isVoid = event?.status === 'Closed';

  return (
    <div className="intake-fullscreen">
      <div className="intake-fullscreen-top">
        <button type="button" className="intake-fullscreen-back" onClick={back}>
          ← Back to event
        </button>
        {event ? <span className="intake-fullscreen-event">{event.name}</span> : null}
      </div>
      <div className="intake-fullscreen-body">
        <div className="intake-fullscreen-card">
          {!user ? (
            <p className="page-subtitle">Log in to fill out this form.</p>
          ) : !Number.isInteger(idNum) || idNum < 1 ? (
            <p className="create-order-error" role="alert">
              Invalid event.
            </p>
          ) : loading ? (
            <p className="page-subtitle">Loading…</p>
          ) : error ? (
            <p className="create-order-error" role="alert">
              {error}
            </p>
          ) : (
            <EventIntakeFill eventId={idNum} form={form} fields={fields} isVoid={isVoid} />
          )}
        </div>
      </div>
    </div>
  );
};

export default EventIntakeFullScreen;
